import type { EntityState } from '@reduxjs/toolkit';
import {
	createAsyncThunk,
	createEntityAdapter,
	createSlice,
} from '@reduxjs/toolkit';
import { ApiRequestError, ApiServerError, unwrapFetch2 } from '../fetch';
import * as Hub from '../ogm-hub';

export function productLinePriceId(productLine: IProductLinePrice) {
	return [
		productLine.corpSupplierCode,
		productLine.distributionCenterDssiCode,
		productLine.supplierProductNumber,
	].join('|');
}

export type IProductLinePricesState = EntityState<IProductLinePrice>;

export const productLinePricesAdapter = createEntityAdapter<IProductLinePrice>({
	selectId: productLinePriceId,
});

export interface IProductLinePrice {
	productPrices: Hub.ProductPrice[];
	corpSupplierCode: string;
	distributionCenterDssiCode: string;
	supplierProductNumber: string;
}

export interface FetchLineLevelPricesParams {
	blueprintId: number;
	corpSupplierCode: string;
	supplierProductNumber: string;
}

export interface FetchMultipleLineLevelPricesParams {
	blueprintId: number;
	corpSupplierCode: string;
	supplierProductNumbers: string[];
}

export const fetchLineLevelPrices = createAsyncThunk(
	'productLinePrices/fetchLineLevelPrices',
	async (params: FetchLineLevelPricesParams, { rejectWithValue }) => {
		try {
			const result = await unwrapFetch2(
				Hub.PriceController.GetProductLinePrices(
					params.blueprintId,
					params.supplierProductNumber
				)
			);
			return result.map((productLinePrice) => ({
				...productLinePrice,
				corpSupplierCode: params.corpSupplierCode,
				supplierProductNumber: params.supplierProductNumber,
			}));
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const fetchMultipleLineLevelPrices = createAsyncThunk(
	'productLinePrices/fetchMultipleLineLevelPrices',
	async (params: FetchMultipleLineLevelPricesParams, { rejectWithValue }) => {
		try {
			const result = await unwrapFetch2(
				Hub.PriceController.GetProductLinePricesForMultipleProducts(
					params.blueprintId,
					params.supplierProductNumbers
				)
			);
			return result.map((productLinePrice) => ({
				...productLinePrice,
				corpSupplierCode: params.corpSupplierCode,
			}));
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const productLinePricesSlice = createSlice({
	name: 'productLinePrices',
	initialState: productLinePricesAdapter.getInitialState(),
	reducers: {},
	extraReducers: (builder) => {
		builder.addCase(fetchLineLevelPrices.fulfilled, (state, action) => {
			productLinePricesAdapter.upsertMany(state, action.payload);
		});
		builder.addCase(fetchMultipleLineLevelPrices.fulfilled, (state, action) => {
			productLinePricesAdapter.upsertMany(state, action.payload);
		});
	},
});

export function selectProductLinePrice(state, id: string) {
	return state.productLinePrices.entities[id];
}

export function selectProductLinePrices(state, ids: string[]) {
	return ids.map((id) => selectProductLinePrice(state, id));
}
